import ClearIcon from '@mui/icons-material/Clear';
import queryString from 'query-string';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import Button from '../../components/Button';
import { useOrderContext } from '../../context/OrderContext';
import { useSystemContext } from '../../context/SystemContext/SystemContext';
import WelcomeLoader from './Loader';

interface WelcomeProps {}

const Welcome: React.FC<WelcomeProps> = () => {
  const { updateOrderId, setIsCreate } = useOrderContext();
  const { seatInfo, stadiumInfo, termsAndConditionsData, isLoadingStadium, isLoadingSeat, enabledNewVersion } = useSystemContext();

  const { t } = useTranslation();

  const location = useLocation();
  const queryParams = queryString.parse(location.search);

  const [termsAndConditions, setTermsAndConditions] = useState(false);

  const [showError, setShowError] = useState(false);
  const [showTermsUI, setShowTermsUI] = useState(false);

  useEffect(() => {
    const updateSeatIdAsync = async () => {
      const terms_and_conditions = localStorage.getItem('terms_and_conditions');

      if (!terms_and_conditions) {
        setShowTermsUI(true);
      } else {
        setShowTermsUI(false);
      }
      if (seatInfo?.id && stadiumInfo?.id) {
        try {
          setIsCreate(true);
          await updateOrderId(seatInfo.id);
        } catch (error) {
          return;
        }
      }
    };

    updateSeatIdAsync();
  }, [seatInfo?.id, stadiumInfo?.id]);

  const buildQueryUrl = (url: string, queryParams: Record<string, any>, excludeKeys: string[] = []) => {
    const filteredParams = Object.fromEntries(Object.entries(queryParams).filter(([key]) => !excludeKeys.includes(key)));

    return queryString.stringifyUrl({ url, query: filteredParams });
  };

  const newOrderLink = buildQueryUrl('/neworder', queryParams, ['tab']);

  const trackOrderLink = queryString.stringifyUrl({
    url: `/neworder`,
    query: { ...queryParams, tab: 'orderHistory' },
  });

  const handleAgreeTermsAndConditions = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setTermsAndConditions(checked);
    if (checked) {
      localStorage.setItem('terms_and_conditions', 'active');
      setShowError(false);
    } else {
      localStorage.removeItem('terms_and_conditions');
    }
  };

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    const mustAgree = !!stadiumInfo?.enable_terms && !!showTermsUI;
    const agreed = termsAndConditions || !!localStorage.getItem('terms_and_conditions');
    if (mustAgree && !agreed) {
      e.preventDefault();
      setShowError(true);
    }
  };

  if (isLoadingStadium || isLoadingSeat) {
    return <WelcomeLoader />;
  }

  return (
    <div className="bg-black-1 h-full w-full px-4 flex items-center justify-center">
      <div className="bg-white px-4 py-5 w-full max-w-2xl rounded-xl shadow-modalWhite">
        <div className="flex gap-1 flex-col pb-3">
          <div className="flex justify-between">
            <p className="text-lg font-semibold leading-7 not-italic text-black-900">{enabledNewVersion ? t('MEJI_WELCOME') : t('WELCOME_TITLE')}</p>
            <Link to={newOrderLink} onClick={handleLinkClick}>
              <button>
                <ClearIcon />
              </button>
            </Link>
          </div>
          <p className="text-sm not-italic font-normal leading-5 text-black-600 my-4">{t('WELCOME_DESCRIPTION')}</p>
        </div>

        {stadiumInfo?.enable_terms && termsAndConditionsData && showTermsUI && (
          <div className="flex flex-col gap-3 mb-8">
            <div dangerouslySetInnerHTML={{ __html: termsAndConditionsData }} />
            <div className="flex items-center gap-2">
              <input
                className={`w-6 h-6 border-2 rounded-md focus:outline-none ${showError ? 'border-red-500' : 'border-gray-300'}`}
                type="checkbox"
                id="terms_and_conditions"
                checked={termsAndConditions}
                onChange={handleAgreeTermsAndConditions}
                style={
                  showError
                    ? {
                        appearance: 'none',
                        MozAppearance: 'none',
                        WebkitAppearance: 'none',
                      }
                    : {}
                }
              />
              <p className={`text-sm not-italic font-normal leading-5 ${showError ? 'text-red-500' : 'text-black-600'}`}>{t('AGREE_TERMS_AND_CONDITIONS')}</p>
            </div>
          </div>
        )}

        <div className="flex flex-col gap-3 pt-3">
          <Link to={newOrderLink} onClick={handleLinkClick}>
            <Button type="secondary">{t('WELCOME_BUTTON_NEWORDER')}</Button>
          </Link>

          <Link to={trackOrderLink} onClick={handleLinkClick}>
            <Button type="primary">{t('WELCOME_BUTTON_TRACKORDER')}</Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Welcome;
